import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Store } from '@ngxs/store';
import { UtmState } from '../states/utm.state';
import { AnalyticsService } from './analytics.service';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { DecimalPipe } from '@angular/common';
import { SharedService } from './shared.service';
import { JobCriteria, JobStatus, PaymentProvider, SearchParams } from '../common';
import { Utility } from '../core/common';

@Injectable({
  providedIn: 'root'
})
export class JobService {

  BasePrice = 70000;
  services: any[] = [
    { min: 0, max: this.BasePrice, step: 5000 },
    {
      min: this.BasePrice, max: 200000, step: 10000,
      // email: 10, cv: 1
    },
    {
      min: 200000, max: 350000, step: 50000,
      // email: 75, cv: 7, sms: 25, telegram_group: 1, instagram_post: true
    },
    {
      min: 350000, step: 50000,
      // email: 100, cv: 10, sms: 40, telegram_group: 1, telegram_post: true, instagram_post: true, instagram_story: true, reportage: true, push: 100
    }
  ];


  constructor(private store: Store, private http: HttpClient, private toastr: ToastrService, private numberPipe: DecimalPipe,
    private analyticsService: AnalyticsService, private router: Router) { }

  async count(state: string = undefined, city: string = undefined) {
    var loc = '';
    if (state) loc += `${encodeURIComponent(state)}/`
    if (city) loc += `${encodeURIComponent(city)}/`
    return this.http.get<number>(environment.app.apiUrl + `job/count/${loc}`).toPromise();
  }
  async search(criteria: JobCriteria, from = 0, limit = 10) {
    var params = Utility.sortProperties(JSON.parse(JSON.stringify(criteria)));
    return this.http.get<{ total: number, jobs: any[] }>(environment.app.apiUrl + `job/search/${from}/${limit}`, { params }).toPromise();
  }
  async postSearch(criteria: JobCriteria, from = 0, limit = 10) {
    return this.http.post<{ total: number, jobs: any[] }>(environment.app.apiUrl + `job/search/${from}/${limit}`, criteria).toPromise();
  }

  searchCounts(searches: any[]) {
    return this.http.post<any[]>(environment.app.apiUrl + `job/search/counts`, searches).toPromise();
  }

  aggregate(criteria: JobCriteria, field: string, count?: number) {
    var params = JSON.parse(JSON.stringify(criteria));
    return this.http.get<any>(environment.app.apiUrl + `job/aggregate/${field}/${count || ''}`, { params }).toPromise();
  }

  nearby(where: string, what: string, distance = 100, omitCity = false, from = 0, limit = 10) {
    return this.http.get<{ total: number, jobs: any[] }>(environment.app.apiUrl + 'job/nearby', {
      params: {
        l: where || '', q: what || '', from: from.toString(), limit: limit.toString(),
        distance: distance.toString(), omitCity: omitCity.toString()
      }
    }).toPromise();
  }

  related(jobId: string, limit = 10) {
    return this.http.get<any[]>(environment.app.apiUrl + 'job/related/' + jobId + '/' + limit).toPromise();
  }

  getAdmin(id: string) {
    return this.http.get<any>(environment.app.apiUrl + 'job/admin/' + id, { params: { cb: Date.now() } }).toPromise();
  }

  get(id: string) {
    return this.http.get<any>(environment.app.apiUrl + 'job/' + id).toPromise();
  }
  getByUrl(url: string): any {
    return this.http.post<any>(environment.app.apiUrl + 'job/by-url', { url }).toPromise();
  }
  getTarget(idOrUrl: string): any {
    return this.http.post<any>(environment.app.apiUrl + 'job/target', { idOrUrl }).toPromise();
  }

  getForAgent(id: string) {
    return this.http.get<any>(environment.app.apiUrl + 'job/agent/' + id).toPromise();
  }

  getByCode(code: number) {
    return this.http.get<any>(environment.app.apiUrl + 'job/code/' + code).toPromise();
  }

  getMine(id: string) {
    return this.http.get<any>(environment.app.apiUrl + 'job/mine/' + id).toPromise();
  }

  mine(status?: JobStatus, expired?: boolean, from = 0, limit = 10) {
    return this.http.post<{ jobs: any[], total: number }>(environment.app.apiUrl + 'job/mine', { status, expired, from, limit }).toPromise();
  }

  agentList(status?: JobStatus, from = 0, limit = 10) {
    return this.http.post<{ jobs: any[], total: number }>(environment.app.apiUrl + 'job/agent', { status, from, limit }).toPromise();
  }

  searchEmployer(where: string, what: string, from = 0, limit = 10) {
    return this.http.get<{ total: number, users: any[] }>(environment.app.apiUrl + 'job/search/employer', {
      params: { l: where || '', q: what || '', from: from.toString(), limit: limit.toString() }
    }).toPromise();
  }

  searchState(what?: string) {
    return this.http.get<any[]>(environment.app.apiUrl + 'job/search/state', { params: { q: what || '' } }).toPromise();
  }

  remoteByState() {
    return this.http.get<any[]>(environment.app.apiUrl + 'job/remote/state').toPromise();
  }

  // operator
  admin_search(criteria: JobCriteria, from = 0, limit = 10) {
    return this.http.post<{ jobs: any[], total: number }>(environment.app.apiUrl + 'job/admin/search', { criteria, from, limit }).toPromise();
  }

  // operator
  setDeleted(id: string, isDeleted = true) {
    return this.http.delete(environment.app.apiUrl + 'job/' + id + '/' + isDeleted).toPromise();
  }

  async insert(job: any) {
    this.analyticsService.gtmEvent('post-job', 'employer', job.PackageKey);
    job.UtmParams = this.store.selectSnapshot(UtmState.utm);
    return this.http.post(environment.app.apiUrl + 'job/insert', job).toPromise();
  }

  // employer
  update(job: any, imageOrRemoveImage?: File | boolean) {
    const formData = new FormData();
    const removeImage = imageOrRemoveImage == true;
    if (imageOrRemoveImage instanceof File) { formData.append('image', imageOrRemoveImage, imageOrRemoveImage.name); }
    formData.append('job', JSON.stringify(Utility.removeNull(job)));
    return this.http.post(environment.app.apiUrl + 'job/update', formData,
      { params: { removeImage: removeImage.toString() } }).toPromise();
  }

  // employer
  setStatus(id: string, status: JobStatus) {
    return this.http.post(environment.app.apiUrl + 'job/status/' + id + '/' + status, null).toPromise();
  }
  // employer
  saveFree(id: string) {
    return this.http.post(environment.app.apiUrl + 'job/save-free/' + id, null).toPromise();
  }

  // operator
  setStatusByAdmin(id: string, status: JobStatus, data?: any, process = false) {
    return this.http.post(environment.app.apiUrl + 'job/status/' + id + '/' + status + '/admin', data || {},
      { params: { process: process.toString() } }).toPromise();
  }
  // operator
  rejectByCriteria(criteria: any) {
    return this.http.post(environment.app.apiUrl + 'job/reject/admin', criteria).toPromise();
  }

  // operator
  clearance(id: string, invoiceAmount: number) {
    return this.http.post(environment.app.apiUrl + `job/clearance/${id}/${invoiceAmount}`, null).toPromise();
  }

  // operator
  operatorSetImage(jobId: string, imageOrRemoveImage?: File | boolean) {
    const formData = new FormData();
    const removeImage = imageOrRemoveImage == true;
    if (imageOrRemoveImage instanceof File) { formData.append('image', imageOrRemoveImage, imageOrRemoveImage.name); }
    return this.http.post(environment.app.apiUrl + `job/operator/set-image/${jobId}`, formData,
      { params: { removeImage: removeImage.toString() } }).toPromise();
  }
  // operator
  operatorSavePartial(job: any) {
    return this.http.post<any>(environment.app.apiUrl + 'job/operator/save-partial', job).toPromise();
  }

  // agent
  async saveForAgent(job: any, amount: number, imageOrRemoveImage?: File | boolean) {
    const formData = new FormData();
    const removeImage = imageOrRemoveImage == true;
    if (imageOrRemoveImage instanceof File) { formData.append('image', imageOrRemoveImage, imageOrRemoveImage.name); }
    formData.append('job', JSON.stringify(job));
    formData.append('amount', amount.toString());
    const redirect: any = await this.http.post(environment.app.apiUrl + 'job/agent/save', formData,
      { params: { removeImage: removeImage.toString() } }).toPromise();
    if (redirect.link) { location.href = redirect.link; }
  }

  // operator
  // editAll(jobs: any[]) {
  //   return this.http.post(environment.app.apiUrl + 'job/editall', jobs).toPromise();
  // }

  // employer
  renewalQuote(id: string) {
    return this.http.get<{ PackageKey?: string; Amount: number; Days: number }>(`${environment.app.apiUrl}job/renew/${id}`).toPromise();
  }

  async renew(id: string, quote: { PackageKey?: string; Amount: number; Days: number; DiscountId?: string }) {
    const redirect: any = await this.http.post(`${environment.app.apiUrl}job/renew/${id}`, quote).toPromise();
    if (redirect.provider == PaymentProvider.Manual)
      this.router.navigateByUrl(`/employer/paid/${id}?pid=${redirect.id}`)
        .then(() => location.reload());
    else if (redirect.link)
      location.href = redirect.link;
    else
      throw new Error('Payment link unavailable');
  }

  async pay(id: string, amount?: number, discount?: string, manualToken?: string) {
    const redirect: any = await this.http.get(
      `${environment.app.apiUrl}job/pay/${id}/${amount || 0}/${discount || ''}`,
      { params: Utility.removeUndefined({ 'manual': manualToken }) }).toPromise();
    if (redirect.provider == PaymentProvider.Manual)
      this.router.navigateByUrl(`/employer/paid/${id}?pid=${redirect.id}`)
        .then(() => location.reload());
    else if (redirect.link)
      location.href = redirect.link;
  }

  // seeker
  apply(apply: any) {
    apply.UtmParams = this.store.selectSnapshot(UtmState.utm);
    this.analyticsService.gtmEvent('apply-job', 'seeker');
    return this.http.post<any>(environment.app.apiUrl + 'job/apply', apply).toPromise();
  }

  getContact(jobId: string, params?: any) {
    return this.http.get(environment.app.apiUrl + 'job/contact/' + jobId, { params }).toPromise();
  }

  startTasks(jobId: string, tasks: { [key: string]: any }) {
    return this.http.post<any>(environment.app.apiUrl + `job/task/${jobId}`, tasks).toPromise();
  }

  report(jobId: string, reason: string) {
    return this.http.post(environment.app.apiUrl + 'job/report/' + jobId, JSON.stringify(reason)).toPromise();
  }

  _packages: any[];
  packages() {
    return this.http.get<any[]>(environment.app.apiUrl + 'job/package').toPromise();
  }
  // operator
  allPackages() {
    return this.http.get<any[]>(environment.app.apiUrl + 'job/package/all').toPromise();
  }
  savePackage(_package: any, image?: File) {
    const formData = new FormData();
    if (image) { formData.append('image', image, image.name); }
    formData.append('package', JSON.stringify(_package));
    return this.http.post(environment.app.apiUrl + 'job/package', formData).toPromise();
  }
  deletePackage(id: string) {
    return this.http.delete(environment.app.apiUrl + 'job/package/' + id).toPromise();
  }

  // operator
  deleteTemplate(id: string) {
    return this.http.delete(environment.app.apiUrl + 'job/template/' + id).toPromise();
  }
  // operator
  saveTemplate(template: any) {
    return this.http.post(environment.app.apiUrl + 'job/template', template).toPromise();
  }
  templates() {
    return this.http.get<any[]>(environment.app.apiUrl + 'job/template').toPromise();
  }
  campaignData(campaign: string): any {
    return this.http.get<any>(environment.app.apiUrl + `job/data/campaign/${campaign}`).toPromise();
  }

  findByUrl(...urls: string[]) {
    return this.http.post<{ [url: string]: any[] }>(environment.app.apiUrl + `job/source-url`, urls).toPromise();
  }

  getIndexData(criteria: any) {
    return this.http.get<any[]>(environment.app.apiUrl + 'job/index/data', { params: criteria }).toPromise();
  }

  setIndexData(item: any) {
    return this.http.post(environment.app.apiUrl + 'job/index/data', item).toPromise();
  }

  story() {
    return this.http.get<any[]>(environment.app.apiUrl + `job/story`).toPromise();
  }
  adminTagdJobTitles(id: any) {
    return this.http.post<any>(environment.app.apiUrl + `job/admin/tag-jobtitles/${id}`, null).toPromise();
  }

  // refineInstagram(type: JobStatus) {
  //   return this.http.get<any[]>(environment.app.apiUrl + `job/index/instagram/refine/${type}`).toPromise();
  // }

  setSearchTag(id: string, searchedtag: any) {
    return this.http.post<any>(environment.app.apiUrl + `job/search-tag/${id}`, searchedtag, { context: SharedService.NO_LOADING_BAR }).toPromise();
  }
  changeOwner(id: any, username: string): any {
    return this.http.post<any>(environment.app.apiUrl + `job/change-owner/${id}/${username}`, null).toPromise();
  }

  static getTaggedJobTitles(job: any): string[] {
    if (job._metaJobTitleTags?.length) {
      return job._metaJobTitleTags.sort((a, b) => b.Relevance - a.Relevance).map(_ => _.Title);
    }
    return [];
  }

  static getSearchQuery(job: any): string {
    if (!job)
      return '';
    let jobTitles = this.getTaggedJobTitles(job);
    if (jobTitles.length)
      return jobTitles[0];
    var stopWords = ['آگهی', 'استخدام', 'شرکت', 'جذب', 'در', 'با'];
    var words = job.Title?.split(/[\s|(|)|\-|_|+|\[|\]|\|]/gm);
    return words?.filter(_ => _.trim() && !stopWords.includes(_)).slice(0, 4).join(' ');
  }

  static getExpiredRedirect(job: any, addR = true): string {
    if (!job)
      return '/job' + (addR ? '?r=notfound' : '');
    const searchQuery = this.getSearchQuery(job) || '';
    return `/job;l=${encodeURIComponent(Utility.join('،', job.State, job.City))};q=${encodeURIComponent(searchQuery)}` +
      (addR ? '?r=expired' : '');
  }

  static getPageTitle(params: SearchParams, addEstekhdam: boolean = true, addGlobal?: boolean): string {
    if (!params) return '';
    params.q = params.q || '';
    if (addEstekhdam)
      params.q = Utility.removeWords(params.q, ['جذب', 'استخدام'])
    let title = `${addEstekhdam ? 'آگهی استخدام' : ''}${params.q ? ' ' + params.q : ''}` +
      (params.g == '0' ? ' آقا' : (params.g == '1' ? ' خانم' : '')) +
      (params.remote ? ' دورکاری' : '') +
      (params.jt?.length ? ' ' + (typeof (params.jt) == 'string' ? params.jt : params.jt.join(' ')) : '') +
      (params.sal ? ` با حقوق بالای ${params.sal / 1000000} میلیون` : '') +
      (params.noexp ? ' بدون سابقه' : '') +
      (params.insurance ? ' با بیمه' : '') +
      (params.c?.length ? ' در شرکت ' + (params.c.join ? params.c.join(' ') : params.c) : '') +
      (params.src ? ' در سایت ' + params.src : '') +
      ` در ${Utility.locationString(params.state, params.city, null, addGlobal && !params.dist)}` +
      (params.dist ? ` (تا ${params.dist} کیلومتر)` : '');

    return title;
  }

  static getSearchedTags(job: any): string[] {
    return Utility.distinct(job._metaSearchedTags.sort((a, b) => b.Click - a.Click).map(_ => _.Keyword).filter(_ => isNaN(_)));
  }
  static getSearchUrlParams(params: any): { params?: any, queryParams?: any } {
    if (!params) return null;
    params = Utility.sortProperties(Utility.removeEmpty(params));

    let result: { params?: any, queryParams?: any } = { params: {}, queryParams: {} };

    result.params.l = params.l;
    if (params.state || params.city) {
      result.params.l = Utility.locationSearch(params.state, params.city);
      delete params.state;
      delete params.city;
    }

    result.params.q = params.q;

    for (var p in params)
      if (p != 'l' && p != 'q' && p != 'Location' && !p.startsWith('_') && (params[p]?.toString())) {
        let val = params[p];
        if (val instanceof Array)
          val = val.join(',').trim();
        if (!(val?.toString())) continue;
        result.queryParams[p] = val;
      }

    result.params = Utility.sortProperties(Utility.removeEmpty(result.params));
    result.queryParams = Utility.sortProperties(Utility.removeEmpty(result.queryParams));

    return result;
  }

  lastSearch = new SearchParams();

  agentSaveAgentData(jobId: string, agentData: any) {
    return this.http.post<any>(environment.app.apiUrl + `job/agent/save-data`, { jobId, agentData }).toPromise();
  }

  karpashaSeekerJobs(seekerId: string, from = 0, limit = 10, getTasks = true) {
    return this.http.post<{ total: number, jobs: any[], tasks: any[] }>(environment.app.apiUrl + `job/karpasha/seeker-jobs`, { seekerId, from, limit, getTasks }).toPromise();
  }

  getJobSummaryFields(job: any): { key: string, value: string }[] {
    return [
      {
        key: 'حقوق', value: (job.MinSalary || job.MaxSalary)
          ? 'حقوق ' + [job.MinSalary ? this.numberPipe.transform(job.MinSalary) : undefined, job.MaxSalary ? this.numberPipe.transform(job.MaxSalary) : undefined].join(' - ').replace(/^[\s-]+/, '').replace(/[\s-]+$/, '') + ` تومان`
          : undefined
      },
      // {
      //   key: 'پرداخت', value: job.PaymentTypeString
      //     || Utility.distinct(<string[]>job.PaymentText)?.splice(0, 3)?.join(' و ')
      // },
      { key: 'بیمه', value: job.HasInsurance ? 'بیمه دارد' : undefined },
      { key: 'دورکاری', value: job.IsRemote ? 'امکان دورکاری' : undefined },
      { key: 'جنسیت', value: job.Gender == 0 ? 'فقط آقا' : job.Gender == 1 ? 'فقط خانم' : undefined },
      { key: 'نحوه همکاری', value: job.JobTypes?.join(' - ') },
      { key: 'تحصیلات', value: job.Educations?.length ? 'مدرک ' + job.Educations?.join(' - ') : undefined },
      { key: 'سابقه کاری', value: job.MinExperienceYear > 0 ? `${job.MinExperienceYear} سال سابقه` : job.MinExperienceYear == 0 ? 'بدون نیاز به سابقه' : undefined },
      { key: 'تعداد مورد نیاز', value: job.Vacancies > 1 ? `استخدام ${job.Vacancies} نفر` : undefined },
      {
        key: 'بازه سنی', value: (job.MinAge || job.MaxAge)
          ? (job.MinAge ? `از ${job.MinAge} ` : '') + (job.MaxAge ? `تا ${job.MaxAge}` : '') + ' ساله'
          : undefined
      },
      { key: 'مزایا', value: job.Bonuses?.join(' - ') },
    ].filter(_ => _.value);
  }

  removeKarpashaData(jobId: string, id: string) {
    return this.http.delete(environment.app.apiUrl + `job/karpasha/remove-data/${jobId}/${id}`).toPromise();
  }

  async getAdvertises() {
    return this.http.get<any[]>(environment.app.apiUrl + `job/advertise/all`).toPromise();
  }
  async getAdvertise(id: string) {
    return this.http.get<{ advertise: any, jobs: any }>(environment.app.apiUrl + `job/advertise/${id}`).toPromise();
  }
  async saveAdvertise(advertise: any) {
    return this.http.post<any>(environment.app.apiUrl + `job/advertise/save`, advertise).toPromise();
  }
  getSuggestedAdvertises(jobCriteria: JobCriteria): any {
    return this.http.post<any[]>(environment.app.apiUrl + `job/advertise/suggested`, { jobCriteria }).toPromise();
  }
  cloneJob(jobId: string, jobTitle: string) {
    return this.http.post<any>(environment.app.apiUrl + `job/admin/clone-job`, { jobId, jobTitle }).toPromise();
  }
  getClonedJobs(jobId: string) {
    return this.http.post<any[]>(environment.app.apiUrl + `job/admin/cloned-jobs/${jobId}`, null).toPromise();
  }
}
